import { DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { FieldDef } from '@core/models';
import { Store } from '@ngxs/store';
import { RelatedToType } from '@shared/enums';
import { TaskActions } from '@tasks/state/actions';
import { TaskState } from '@tasks/state/state';
import { Task, TaskRelatedToModule, TaskType } from '@tasks/tasks.types';
import { TaskUtils } from '@tasks/tasks.utils';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';

import { Component } from '@angular/core';
import { LinkKeyDateResult } from '@shared/modals/link-key-date-modal/link-key-date-modal.component';

@Component({
  selector: 'app-task-view-base',
  template: '',
})
export class TaskViewBase implements OnInit {
  protected store = inject(Store);
  protected destroyRef = inject(DestroyRef);
  protected router = inject(Router);
  protected activatedRoute = inject(ActivatedRoute);
  taskFields: FieldDef[] = [];
  routeData;
  isGlobalTask = false;
  isAggregatedTask = false;
  isTaskListTemplate = false;

  constructor() {
    this.isAggregatedTask =
      this.store.selectSnapshot(TaskState.getTaskRelatedTo).relatedToType.toLocaleLowerCase() === 'aggregatedtasks';
    this.isGlobalTask =
      this.store.selectSnapshot(TaskState.getTaskRelatedTo).relatedToType.toLocaleLowerCase() === 'account';
    this.isTaskListTemplate =
      this.store.selectSnapshot(TaskState.getTaskRelatedTo).relatedToType === TaskRelatedToModule.TaskListTemplate;

    this.taskFields = this.store.selectSnapshot(
      ObjectsManagerState.getFieldsByObjectName(RelatedToType.Task, {
        allowedFieldsOnly: true,
        addNestedFields: false,
      }),
    );

    const type =
      (this.isTaskListTemplate
        ? this.store.selectSnapshot(TaskState.getTaskView)?.applicable_type
        : this.store.selectSnapshot(TaskState.getTaskView)?.related_to_type) ??
      this.store.selectSnapshot(TaskState.getTaskRelatedTo)?.relatedToType;

    this.taskFields = TaskUtils.getApplicableFields(type, this.taskFields);

    this.activatedRoute.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      this.routeData = { ...data, taskFieldDefs: this.taskFields };
    });
  }

  ngOnInit(): void {}

  openTaskDetail(task: Task, showComments = false) {
    this.store
      .dispatch(new TaskActions.GetTaskDetail(task.task_list_id, task.id))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.redirectToTask(task, showComments);
      });
  }

  redirectToTask(task: Task, showComments = false): void {
    // it's necessary until the SC-45927 is completed
    if (showComments) {
      this.router.navigate([{ outlets: { details: ['details', task.id] } }], {
        relativeTo: this.activatedRoute.parent,
        fragment: `comments-${task.id}`,
      });
    } else {
      this.router.navigate([{ outlets: { details: ['details', task.id] } }], {
        relativeTo: this.activatedRoute.parent,
      });
    }
  }

  createTask(task: {
    taskType: TaskType;
    parentId?: number;
    rank?: number;
    linked_key_date_field_as_source?: string;
    linked_key_date_field_as_target?: string;
    title?: string;
  }) {
    this.store.dispatch(new TaskActions.CreateTask(task as any));
  }
}
